import { TypeStarRating } from "@contentful/types";
import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "@yardzen/ui/components/link";
import { cn } from "@yardzen/ui/lib/utils";
import { cva } from "class-variance-authority";
import {
  hasYardzenReviewCount,
  REVIEWS_PAGE_HREF,
  withCurrentReviewCount,
} from "./reviewSummary";
import { StarPill } from "./StarPill";
import type { ReactNode } from "react";

export function StarRating({
  starRating,
  variant,
  textColorClass,
  containerClassName,
}: {
  starRating: TypeStarRating<"WITHOUT_UNRESOLVABLE_LINKS">;
  variant: "left-aligned-small" | "left-aligned" | "center";
  textColorClass?: string;
  containerClassName?: string;
}) {
  const pill = starRating.fields.pill ?? "none";
  const starColor = starRating.fields.starColor ?? "gold";
  const resolvedStarColor = pill === "badge" ? "black" : starColor;
  const parsed = parseInt(starRating.fields.numberOfStars ?? "", 10);
  const starCount =
    Number.isFinite(parsed) && parsed >= 0 && parsed <= 5 ? parsed : 1;

  // A rating that carries the site-wide review count is Yardzen's summary:
  // it shows the current count and links to /reviews.
  // Per-item ratings (competitors, contractors) render as authored.
  const isReviewSummary = hasYardzenReviewCount(starRating.fields.starText);
  const starText =
    isReviewSummary && starRating.fields.starText
      ? withCurrentReviewCount(starRating.fields.starText)
      : starRating.fields.starText;
  const linkToReviews = (rating: ReactNode) =>
    isReviewSummary ? (
      <Link href={REVIEWS_PAGE_HREF} className="inline-flex">
        {rating}
      </Link>
    ) : (
      rating
    );

  // Blur pill matches the Reviews chip — delegate to the shared primitive.
  if (pill === "blur") {
    return linkToReviews(
      <StarPill
        count={starCount}
        text={starText}
        color={resolvedStarColor}
        className={cn(containerClassName)}
        textClass={cn("font-display text-sm", textColorClass ?? "text-white")}
      />,
    );
  }

  const classes = cva("", {
    variants: {
      wrapper: {
        "left-aligned-small": "flex items-center justify-start gap-1",
        "left-aligned": "flex items-center justify-start gap-2",
        center: "flex items-center justify-center gap-2",
      },
      pill: {
        none: "",
        blur: "rounded-full px-4 py-2 bg-opacity-10 backdrop-blur-2xl",
        green: "rounded-full px-4 py-2 bg-brand-moss-greens-10",
        badge:
          "rounded-full px:1 gap-1 md:px-3 py-1 bg-brand-greens-light text-brand-primary-darker md:gap-2",
      },
      starColor: {
        black: "fill-[#000] text-[#000]",
        gold: "fill-scheme-star text-scheme-star",
      },
      starSize: {
        "left-aligned-small": "text-[9px] text-xs",
        "left-aligned": "text-base",
        center: "text-base",
      },
      textSize: {
        "left-aligned-small": "text-xs font-semibold",
        "left-aligned": "text-base font-semibold",
        center: "text-sm",
      },
      textColor: {
        none: "",
        blur: "text-white",
        green: "text-white",
        badge: "text-[9px] md:text-xs text-brand-primary-darker",
      },
    },
  });

  return linkToReviews(
    <div
      className={cn(
        classes({ wrapper: variant }),
        containerClassName ?? classes({ pill }),
      )}
      aria-label="rating"
    >
      {starCount > 0 && (
        <div
          className={cn(
            "flex items-center gap-1",
            pill === "badge"
              ? "text-[10px] text-xs"
              : classes({ starSize: variant }),
          )}
        >
          {Array.from({ length: starCount }).map((_, i) => (
            <FontAwesomeIcon
              key={i}
              icon={faStar}
              className={classes({ starColor: resolvedStarColor })}
              aria-hidden="true"
            />
          ))}
        </div>
      )}

      {starText && (
        <div
          className={cn(
            pill !== "badge" && "font-display",
            classes({ textSize: variant }),
            textColorClass ??
              classes({
                textColor: pill as "none" | "blur" | "green" | "badge",
              }),
          )}
        >
          {starText}
        </div>
      )}
    </div>,
  );
}
